import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryCache, MutationCache } from "@tanstack/react-query";
import { Sentry } from "@/lib/sentry";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { UpdatePrompt } from "@/components/ui/update-prompt";
import { OfflineIndicator } from "@/components/ui/offline-indicator";
import { SessionTimeoutManager } from "@/components/SessionTimeoutManager";
import { queryPersister, OFFLINE_QUERY_KEYS } from "@/lib/offline";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import Orders from "./pages/Orders";
import OrderDetail from "./pages/OrderDetail";
import OrdersMap from "./pages/OrdersMap";
import MyInstallations from "./pages/MyInstallations";
import Installations from "./pages/Installations";
import Customers from "./pages/Customers";
import Cemeteries from "./pages/Cemeteries";
import CemeteriesMap from "./pages/CemeteriesMap";
import Partners from "./pages/Partners";
import Products from "./pages/Products";
import Materials from "./pages/Materials";
import NotFound from "./pages/NotFound";
import ProofApproval from "./pages/ProofApproval";
import CemeteryApproval from "./pages/CemeteryApproval";
import OrderTracking from "./pages/OrderTracking";
import Loads from "./pages/Loads";
import LoadsCreate from "./pages/LoadsCreate";
import MyLoads from "./pages/MyLoads";
import ProductConfiguration from "./pages/ProductConfiguration";
import PricingRules from "./pages/PricingRules";
import EmailTemplates from "./pages/EmailTemplates";
import CalendarPage from "./pages/Calendar";
import CuttingList from "./pages/CuttingList";
import Reports from "./pages/Reports";
import ApiUsage from "./pages/ApiUsage";
import Users from "./pages/Users";

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      // Don't report auth errors (handled by auth flow)
      if (error instanceof Error) {
        const msg = error.message;
        if (msg.includes("401") || msg.includes("403") || msg.includes("JWT")) return;
      }
      Sentry.captureException(error, { level: "warning" });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      Sentry.captureException(error, {
        fingerprint: ["mutation-error", mutation.options.mutationKey?.toString() || "unknown"],
      });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,      // 5 minuuttia - data pysyy tuoreena
      gcTime: 1000 * 60 * 60 * 24,   // 24h - tarvitaan pidempi persist-tilassa
      retry: 2,                       // Uudelleenyritykset verkkovirheiden varalta
      refetchOnWindowFocus: false,    // Ei automaattista uudelleenhakua ikkunan fokuksessa
    },
  },
});

// Persist-asetukset - tallentaa VAIN kriittiset kyselyt IndexedDB:hen
const persistOptions = {
  persister: queryPersister,
  maxAge: 1000 * 60 * 60 * 24,  // 24h max ikä cachelle
  buster: 'v3',                  // Cache-versio (bustataan tarvittaessa)
  dehydrateOptions: {
    shouldDehydrateQuery: (query: { queryKey: readonly unknown[] }) => {
      // Tallenna VAIN nämä query-avaimet
      const queryKey = query.queryKey[0];
      if (typeof queryKey !== 'string') return false;
      return OFFLINE_QUERY_KEYS.includes(queryKey as typeof OFFLINE_QUERY_KEYS[number]);
    },
  },
};

const App = () => (
  <ErrorBoundary>
    <PersistQueryClientProvider 
      client={queryClient}
      persistOptions={persistOptions}
    >
      <TooltipProvider>
        <AuthProvider>
          <Sonner />
          <OfflineIndicator />
          <UpdatePrompt />
          <BrowserRouter>
            <SessionTimeoutManager />
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/orders"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <Orders />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/orders/:id"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <OrderDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/orders/map"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <OrdersMap />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customers"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <Customers />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cemeteries"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <Cemeteries />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cemeteries/map"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <CemeteriesMap />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/partners"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <Partners />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <Products />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/materials"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <Materials />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/configuration"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <ProductConfiguration />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/configuration/pricing"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <PricingRules />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/configuration/email-templates"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <EmailTemplates />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/calendar"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <CalendarPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cutting-list"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <CuttingList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <Reports />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/api-usage"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <ApiUsage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <Users />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/installations"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <Installations />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/loads"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <Loads />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/loads/create"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <LoadsCreate />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/loads/mine"
                element={
                  <ProtectedRoute allowedRoles={["admin", "installer"]}>
                    <MyLoads />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/installations/mine"
                element={
                  <ProtectedRoute allowedRoles={["admin", "installer"]}>
                    <MyInstallations />
                  </ProtectedRoute>
                }
              />
              {/* Public proof approval page - no auth required */}
              <Route path="/proofs/:token" element={<ProofApproval />} />
              {/* Public cemetery approval page - no auth required */}
              <Route path="/cemetery-approval/:token" element={<CemeteryApproval />} />
              {/* Public order tracking page - no auth required */}
              <Route path="/track/:orderNumber/:token" element={<OrderTracking />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </TooltipProvider>
    </PersistQueryClientProvider>
  </ErrorBoundary>
);

export default App;
